import { useState, useEffect, useMemo } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  Plus, Search, Download, Trash2, Building2, Briefcase, Receipt, 
  IndianRupee, TrendingUp, Calendar, Filter, Edit2, Eye, X,
  Landmark, Hammer, Truck, Users, Lightbulb, Megaphone, Car, 
  FileText, Coffee, CreditCard, Banknote, ChevronDown
} from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

const EXPENSE_TYPES = [
  { value: 'site', label: 'Site/Project', icon: Building2, color: 'bg-amber-500', lightColor: 'bg-amber-50 text-amber-700 border-amber-200' },
  { value: 'office', label: 'Office/Operations', icon: Briefcase, color: 'bg-blue-500', lightColor: 'bg-blue-50 text-blue-700 border-blue-200' },
  { value: 'miscellaneous', label: 'Miscellaneous', icon: Receipt, color: 'bg-purple-500', lightColor: 'bg-purple-50 text-purple-700 border-purple-200' },
];

const SITE_CATEGORIES = [
  'Construction Material', 'Labor/Worker Salary', 'Site Development', 'Land Cost',
  'Approval/NOC Fees', 'Architect/Design', 'Plumbing', 'Electrical Work',
  'Transport/Logistics', 'Security', 'Equipment Rent', 'Other Site Expense'
];
const OFFICE_CATEGORIES = [
  'Office Rent', 'Electricity Bill', 'Internet/Phone', 'Stationery',
  'Marketing/Advertising', 'Staff Salary', 'Office Maintenance', 'Software/IT',
  'Printing', 'Courier/Postage', 'Other Office Expense'
];
const MISC_CATEGORIES = [
  'Travel', 'Client Entertainment', 'Legal Fees', 'Insurance',
  'Registration/Stamp', 'Donation', 'Penalty/Fine', 'Medical',
  'Vehicle Maintenance', 'Other'
];

const getCategoriesForType = (type) => {
  if (type === 'site') return SITE_CATEGORIES;
  if (type === 'office') return OFFICE_CATEGORIES;
  return MISC_CATEGORIES;
};

const PAYMENT_MODES = ['Cash', 'UPI', 'Bank Transfer', 'Cheque', 'Credit Card', 'Other'];

const typeConfig = (t) => EXPENSE_TYPES.find(e => e.value === t) || EXPENSE_TYPES[2];

export default function Expenses({ user, onLogout }) {
  const [expenses, setExpenses] = useState([]);
  const [properties, setProperties] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editExpense, setEditExpense] = useState(null);
  const [viewExpense, setViewExpense] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [monthFilter, setMonthFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const defaultForm = {
    expense_type: 'site', category: '', amount: '', paid_by: '', purpose: '',
    description: '', property_id: '', property_name: '', vendor_name: '',
    payment_mode: 'Cash', receipt_number: '', date: new Date().toISOString().split('T')[0]
  };
  const [formData, setFormData] = useState(defaultForm);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setIsDataLoading(true);
    try {
      const [expRes, propRes, sumRes] = await Promise.allSettled([
        api.get('/expenses'), api.get('/properties'), api.get('/expenses/summary')
      ]);
      if (expRes.status === 'fulfilled') setExpenses(expRes.value.data);
      if (propRes.status === 'fulfilled') setProperties(propRes.value.data || []);
      if (sumRes.status === 'fulfilled') setSummary(sumRes.value.data);
    } catch {} finally { setIsDataLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.category || !formData.amount) { toast.error('Category aur amount zaroori hai'); return; }
    setLoading(true);
    try {
      const payload = { ...formData, amount: parseFloat(formData.amount), date: new Date(formData.date).toISOString() };
      if (editExpense) {
        await api.put(`/expenses/${editExpense.id}`, payload);
        toast.success('Expense updated!');
      } else {
        await api.post('/expenses', payload);
        toast.success('Expense added!');
      }
      setDialogOpen(false);
      setEditExpense(null);
      setFormData(defaultForm);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    } finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Expense delete karein?')) return;
    try {
      await api.delete(`/expenses/${id}`);
      toast.success('Deleted!');
      setSelectedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
      fetchAll();
    } catch { toast.error('Delete failed'); }
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.size || !window.confirm(`${selectedIds.size} expenses delete karein?`)) return;
    try {
      await api.post('/expenses/bulk-delete', { ids: Array.from(selectedIds) });
      toast.success(`${selectedIds.size} deleted!`);
      setSelectedIds(new Set());
      fetchAll();
    } catch { toast.error('Bulk delete failed'); }
  };

  const openEdit = (exp) => {
    setEditExpense(exp);
    setFormData({
      expense_type: exp.expense_type || 'office',
      category: exp.category, amount: String(exp.amount), paid_by: exp.paid_by || '',
      purpose: exp.purpose || '', description: exp.description || '',
      property_id: exp.property_id || '', property_name: exp.property_name || '',
      vendor_name: exp.vendor_name || '', payment_mode: exp.payment_mode || 'Cash',
      receipt_number: exp.receipt_number || '',
      date: exp.date ? new Date(exp.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
    });
    setDialogOpen(true);
  };

  const openNew = (type = 'site') => {
    setEditExpense(null);
    setFormData({ ...defaultForm, expense_type: type });
    setDialogOpen(true);
  };

  // Filters
  const filtered = useMemo(() => {
    return expenses.filter(exp => {
      if (activeTab !== 'all' && (exp.expense_type || 'office') !== activeTab) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const match = [exp.category, exp.paid_by, exp.purpose, exp.description, exp.property_name, exp.vendor_name]
          .some(f => (f || '').toLowerCase().includes(q));
        if (!match) return false;
      }
      if (monthFilter !== 'all') {
        const d = new Date(exp.date);
        if (`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}` !== monthFilter) return false;
      }
      if (categoryFilter !== 'all' && exp.category !== categoryFilter) return false;
      return true;
    });
  }, [expenses, activeTab, searchQuery, monthFilter, categoryFilter]);

  const filteredTotal = filtered.reduce((s, e) => s + (e.amount || 0), 0);

  const months = useMemo(() => {
    const m = new Set();
    expenses.forEach(e => { const d = new Date(e.date); m.add(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`); });
    return Array.from(m).sort().reverse();
  }, [expenses]);

  const categories = useMemo(() => {
    const c = new Set();
    (activeTab === 'all' ? expenses : expenses.filter(e => (e.expense_type || 'office') === activeTab))
      .forEach(e => c.add(e.category));
    return Array.from(c).sort();
  }, [expenses, activeTab]);

  const toggleSelect = (id) => setSelectedIds(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => { filtered.length === selectedIds.size ? setSelectedIds(new Set()) : setSelectedIds(new Set(filtered.map(e => e.id))); };

  const downloadCSV = () => {
    if (!filtered.length) { toast.error('No data'); return; }
    const headers = ['Date','Type','Category','Property','Vendor','Purpose','Description','Paid By','Payment Mode','Receipt#','Amount'];
    const rows = filtered.map(e => [
      new Date(e.date).toLocaleDateString('en-IN'),
      typeConfig(e.expense_type).label,
      e.category, e.property_name||'', e.vendor_name||'', e.purpose||'', e.description||'',
      e.paid_by||'', e.payment_mode||'Cash', e.receipt_number||'', e.amount
    ]);
    rows.push(['','','','','','','','','','TOTAL', filteredTotal]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `expenses_${activeTab}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success('CSV downloaded!');
  };

  const fmt = (n) => `₹${(n||0).toLocaleString('en-IN')}`;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-5" data-testid="expenses-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Expenses Manager</h1>
            <p className="text-xs text-slate-500 mt-0.5">Site, Office aur Miscellaneous expenses track karein</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={downloadCSV} variant="outline" size="sm" className="gap-1" data-testid="download-csv">
              <Download size={14} /> CSV
            </Button>
            <Button onClick={() => openNew('site')} className="bg-[#0B1120] hover:bg-[#1a2744] gap-1" size="sm" data-testid="add-expense-btn">
              <Plus size={14} /> Add Expense
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            { label: 'Site Expenses', value: summary?.site_total, icon: Building2, color: 'border-l-amber-500 bg-amber-50/50' },
            { label: 'Office Expenses', value: summary?.office_total, icon: Briefcase, color: 'border-l-blue-500 bg-blue-50/50' },
            { label: 'Miscellaneous', value: summary?.misc_total, icon: Receipt, color: 'border-l-purple-500 bg-purple-50/50' },
            { label: 'This Month', value: summary?.this_month, icon: Calendar, color: 'border-l-emerald-500 bg-emerald-50/50' },
            { label: 'Grand Total', value: summary?.grand_total, icon: IndianRupee, color: 'border-l-red-500 bg-red-50/50' },
          ].map((c, i) => (
            <Card key={i} className={`border-l-4 ${c.color} shadow-sm`} data-testid={`summary-card-${i}`}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] sm:text-xs text-slate-500 font-medium">{c.label}</span>
                  <c.icon size={14} className="text-slate-400" />
                </div>
                <div className="text-lg sm:text-xl font-bold text-slate-800">{fmt(c.value)}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Property-wise Site Expenses (if any) */}
        {summary?.property_wise && Object.keys(summary.property_wise).length > 0 && (
          <Card className="shadow-sm" data-testid="property-wise-expenses">
            <CardContent className="p-4">
              <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2"><Building2 size={14} className="text-amber-500" /> Property-wise Site Expenses</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {Object.entries(summary.property_wise).map(([name, data]) => (
                  <div key={name} className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                    <div className="text-xs font-semibold text-slate-700 truncate">{name}</div>
                    <div className="text-base font-bold text-amber-700 mt-1">{fmt(data.total)}</div>
                    <div className="text-[10px] text-slate-500">{data.count} entries</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Type Tabs + Filters */}
        <div className="bg-white rounded-xl border p-3 sm:p-4 shadow-sm space-y-3">
          {/* Tabs */}
          <div className="flex gap-1 overflow-x-auto pb-1 scrollbar-hide" data-testid="expense-type-tabs">
            <button onClick={() => { setActiveTab('all'); setCategoryFilter('all'); }}
              className={`shrink-0 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${activeTab === 'all' ? 'bg-[#0B1120] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
              All ({expenses.length})
            </button>
            {EXPENSE_TYPES.map(t => {
              const count = expenses.filter(e => (e.expense_type || 'office') === t.value).length;
              return (
                <button key={t.value} onClick={() => { setActiveTab(t.value); setCategoryFilter('all'); }}
                  className={`shrink-0 px-3 py-2 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${activeTab === t.value ? `${t.color} text-white` : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                  <t.icon size={12} /> {t.label} ({count})
                </button>
              );
            })}
          </div>

          {/* Filter Row */}
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Search expenses..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                className="pl-9 h-9 text-sm" data-testid="expense-search" />
            </div>
            <select value={monthFilter} onChange={e => setMonthFilter(e.target.value)}
              className="h-9 px-3 text-sm border rounded-md bg-white" data-testid="month-filter">
              <option value="all">All Months</option>
              {months.map(m => <option key={m} value={m}>{new Date(m + '-01').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</option>)}
            </select>
            <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
              className="h-9 px-3 text-sm border rounded-md bg-white" data-testid="category-filter">
              <option value="all">All Categories</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {/* Bulk Actions */}
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <span className="text-xs font-semibold text-red-700">{selectedIds.size} selected</span>
              <Button size="sm" variant="destructive" onClick={handleBulkDelete} className="h-7 text-xs gap-1">
                <Trash2 size={12} /> Delete Selected
              </Button>
            </div>
          )}

          {/* Total Bar */}
          <div className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
            <span className="text-xs text-slate-500">{filtered.length} expenses</span>
            <span className="text-sm font-bold text-slate-800">Total: {fmt(filteredTotal)}</span>
          </div>

          {/* Table */}
          {isDataLoading ? (
            <div className="text-center py-10 text-slate-400 text-sm">Loading expenses...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10">
              <Receipt size={40} className="mx-auto text-slate-200 mb-3" />
              <p className="text-slate-500 text-sm">No expenses found</p>
              <Button onClick={() => openNew(activeTab === 'all' ? 'site' : activeTab)} size="sm" className="mt-3 gap-1 bg-[#0B1120]">
                <Plus size={14} /> Add First Expense
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-8"><input type="checkbox" checked={selectedIds.size === filtered.length && filtered.length > 0} onChange={toggleAll} className="rounded" /></TableHead>
                    <TableHead className="text-xs">Date</TableHead>
                    <TableHead className="text-xs">Type</TableHead>
                    <TableHead className="text-xs">Category</TableHead>
                    <TableHead className="text-xs hidden lg:table-cell">Property/Vendor</TableHead>
                    <TableHead className="text-xs hidden md:table-cell">Purpose</TableHead>
                    <TableHead className="text-xs hidden md:table-cell">Paid By</TableHead>
                    <TableHead className="text-xs hidden lg:table-cell">Mode</TableHead>
                    <TableHead className="text-xs text-right">Amount</TableHead>
                    <TableHead className="text-xs text-center w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(exp => {
                    const tc = typeConfig(exp.expense_type);
                    return (
                      <TableRow key={exp.id} className="hover:bg-slate-50" data-testid={`expense-row-${exp.id}`}>
                        <TableCell><input type="checkbox" checked={selectedIds.has(exp.id)} onChange={() => toggleSelect(exp.id)} className="rounded" /></TableCell>
                        <TableCell className="text-xs text-slate-600 whitespace-nowrap">
                          {new Date(exp.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full border ${tc.lightColor}`}>
                            <tc.icon size={10} /> {tc.label.split('/')[0]}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs font-medium text-slate-800 max-w-[140px] truncate">{exp.category}</TableCell>
                        <TableCell className="text-xs text-slate-500 hidden lg:table-cell max-w-[120px] truncate">
                          {exp.property_name || exp.vendor_name || '-'}
                        </TableCell>
                        <TableCell className="text-xs text-slate-500 hidden md:table-cell max-w-[120px] truncate">{exp.purpose || exp.description || '-'}</TableCell>
                        <TableCell className="text-xs text-slate-500 hidden md:table-cell">{exp.paid_by || '-'}</TableCell>
                        <TableCell className="text-xs text-slate-500 hidden lg:table-cell">{exp.payment_mode || 'Cash'}</TableCell>
                        <TableCell className="text-xs font-bold text-right text-red-600">{fmt(exp.amount)}</TableCell>
                        <TableCell>
                          <div className="flex justify-center gap-1">
                            <button onClick={() => setViewExpense(exp)} className="p-1 hover:bg-slate-100 rounded" data-testid={`view-exp-${exp.id}`}><Eye size={13} className="text-slate-500" /></button>
                            <button onClick={() => openEdit(exp)} className="p-1 hover:bg-blue-50 rounded" data-testid={`edit-exp-${exp.id}`}><Edit2 size={13} className="text-blue-500" /></button>
                            <button onClick={() => handleDelete(exp.id)} className="p-1 hover:bg-red-50 rounded" data-testid={`del-exp-${exp.id}`}><Trash2 size={13} className="text-red-500" /></button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) setEditExpense(null); }}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editExpense ? 'Edit Expense' : 'Add New Expense'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="expense-form">
              {/* Type Selection */}
              <div>
                <Label className="text-xs font-bold mb-2 block">Expense Type *</Label>
                <div className="grid grid-cols-3 gap-2">
                  {EXPENSE_TYPES.map(t => (
                    <button key={t.value} type="button" onClick={() => setFormData({ ...formData, expense_type: t.value, category: '' })}
                      data-testid={`type-${t.value}`}
                      className={`p-3 rounded-xl border-2 text-center transition-all ${
                        formData.expense_type === t.value
                          ? `${t.color} text-white border-transparent shadow-lg scale-[1.02]`
                          : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}>
                      <t.icon size={18} className="mx-auto mb-1" />
                      <span className="text-[10px] font-bold block">{t.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Property (for Site type) */}
              {formData.expense_type === 'site' && (
                <div>
                  <Label className="text-xs">Property/Site *</Label>
                  <select value={formData.property_name} data-testid="expense-property"
                    onChange={e => {
                      const prop = properties.find(p => p.property_name === e.target.value);
                      setFormData({ ...formData, property_name: e.target.value, property_id: prop?.id || '' });
                    }}
                    className="w-full h-9 px-3 text-sm border rounded-md bg-white mt-1">
                    <option value="">-- Select Property --</option>
                    {properties.map(p => <option key={p.id} value={p.property_name}>{p.property_name}</option>)}
                    <option value="__other">Other (Manual Entry)</option>
                  </select>
                  {formData.property_name === '__other' && (
                    <Input className="mt-2" placeholder="Property/Site name type karein" value="" 
                      onChange={e => setFormData({ ...formData, property_name: e.target.value })} />
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                {/* Category */}
                <div>
                  <Label className="text-xs">Category *</Label>
                  <select value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })}
                    className="w-full h-9 px-3 text-sm border rounded-md bg-white mt-1" data-testid="expense-category" required>
                    <option value="">-- Select --</option>
                    {getCategoriesForType(formData.expense_type).map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                {/* Amount */}
                <div>
                  <Label className="text-xs">Amount (₹) *</Label>
                  <Input type="number" value={formData.amount} onChange={e => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0" className="mt-1" data-testid="expense-amount" required />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Date */}
                <div>
                  <Label className="text-xs">Date *</Label>
                  <Input type="date" value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })}
                    className="mt-1" data-testid="expense-date" required />
                </div>
                {/* Payment Mode */}
                <div>
                  <Label className="text-xs">Payment Mode</Label>
                  <select value={formData.payment_mode} onChange={e => setFormData({ ...formData, payment_mode: e.target.value })}
                    className="w-full h-9 px-3 text-sm border rounded-md bg-white mt-1" data-testid="expense-mode">
                    {PAYMENT_MODES.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Vendor */}
                <div>
                  <Label className="text-xs">{formData.expense_type === 'site' ? 'Worker/Vendor Name' : 'Vendor Name'}</Label>
                  <Input value={formData.vendor_name} onChange={e => setFormData({ ...formData, vendor_name: e.target.value })}
                    placeholder="Worker/Vendor ka naam" className="mt-1" />
                </div>
                {/* Paid By */}
                <div>
                  <Label className="text-xs">Paid By</Label>
                  <Input value={formData.paid_by} onChange={e => setFormData({ ...formData, paid_by: e.target.value })}
                    placeholder="Kisne pay kiya" className="mt-1" />
                </div>
              </div>

              {/* Purpose */}
              <div>
                <Label className="text-xs">Purpose</Label>
                <Input value={formData.purpose} onChange={e => setFormData({ ...formData, purpose: e.target.value })}
                  placeholder="Kis liye expense hai" className="mt-1" />
              </div>

              {/* Description */}
              <div>
                <Label className="text-xs">Description / Notes</Label>
                <textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Additional details..." rows={2}
                  className="w-full px-3 py-2 text-sm border rounded-md resize-none mt-1" />
              </div>

              {/* Receipt Number */}
              <div>
                <Label className="text-xs">Receipt/Bill Number</Label>
                <Input value={formData.receipt_number} onChange={e => setFormData({ ...formData, receipt_number: e.target.value })}
                  placeholder="Receipt # (optional)" className="mt-1" />
              </div>

              <Button type="submit" className="w-full bg-[#0B1120] hover:bg-[#1a2744]" disabled={loading} data-testid="submit-expense">
                {loading ? 'Saving...' : editExpense ? 'Update Expense' : 'Add Expense'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* View Detail Dialog */}
        <Dialog open={!!viewExpense} onOpenChange={() => setViewExpense(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Expense Detail</DialogTitle></DialogHeader>
            {viewExpense && (
              <div className="space-y-3" data-testid="expense-detail">
                <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-full border ${typeConfig(viewExpense.expense_type).lightColor}`}>
                  {(() => { const T = typeConfig(viewExpense.expense_type).icon; return <T size={12} />; })()}
                  {typeConfig(viewExpense.expense_type).label}
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-slate-500 text-xs block">Category</span><span className="font-semibold">{viewExpense.category}</span></div>
                  <div><span className="text-slate-500 text-xs block">Amount</span><span className="font-bold text-red-600 text-lg">{fmt(viewExpense.amount)}</span></div>
                  <div><span className="text-slate-500 text-xs block">Date</span><span className="font-medium">{new Date(viewExpense.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</span></div>
                  <div><span className="text-slate-500 text-xs block">Payment Mode</span><span className="font-medium">{viewExpense.payment_mode || 'Cash'}</span></div>
                  {viewExpense.property_name && <div><span className="text-slate-500 text-xs block">Property/Site</span><span className="font-medium">{viewExpense.property_name}</span></div>}
                  {viewExpense.vendor_name && <div><span className="text-slate-500 text-xs block">Vendor/Worker</span><span className="font-medium">{viewExpense.vendor_name}</span></div>}
                  {viewExpense.paid_by && <div><span className="text-slate-500 text-xs block">Paid By</span><span className="font-medium">{viewExpense.paid_by}</span></div>}
                  {viewExpense.receipt_number && <div><span className="text-slate-500 text-xs block">Receipt #</span><span className="font-medium">{viewExpense.receipt_number}</span></div>}
                </div>
                {viewExpense.purpose && <div><span className="text-slate-500 text-xs block">Purpose</span><p className="text-sm">{viewExpense.purpose}</p></div>}
                {viewExpense.description && <div><span className="text-slate-500 text-xs block">Description</span><p className="text-sm text-slate-600">{viewExpense.description}</p></div>}
                <div className="flex gap-2 pt-2">
                  <Button size="sm" variant="outline" onClick={() => { openEdit(viewExpense); setViewExpense(null); }} className="gap-1 flex-1"><Edit2 size={12} /> Edit</Button>
                  <Button size="sm" variant="destructive" onClick={() => { handleDelete(viewExpense.id); setViewExpense(null); }} className="gap-1 flex-1"><Trash2 size={12} /> Delete</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
